import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft, Mail, Copy, CheckCircle2, Loader2, User, Tag, Clock, Save,
} from "lucide-react";

const STATUS_LABEL = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

const STATUS_TONE = {
  new: "bg-amber-100 text-amber-900 border-amber-300",
  in_progress: "bg-blue-100 text-blue-900 border-blue-300",
  resolved: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

export default function AdminEnquiryDetailPage({ superadmin = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);

  const listPath = superadmin ? "/superadmin/enquiries" : "/admin/enquiries";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.adminGetContactSubmission(id);
      setDoc(r.data);
      setStatus(r.data.status || "new");
      setNotes(r.data.admin_notes || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn’t load this enquiry.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const dirty =
    (doc && status !== (doc.status || "new")) ||
    (doc && (notes || "") !== (doc.admin_notes || ""));

  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const payload = {};
      if (status !== (doc.status || "new")) payload.status = status;
      if ((notes || "") !== (doc.admin_notes || "")) payload.admin_notes = notes;
      const r = await api.adminUpdateContactSubmission(id, payload);
      setDoc(r.data);
      setStatus(r.data.status);
      setNotes(r.data.admin_notes || "");
      toast.success("Saved.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Couldn’t save right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(doc?.email || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select the email manually.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="admin-enquiry-detail-loading">
        <Toaster richColors position="top-center" />
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="space-y-4" data-testid="admin-enquiry-detail-error">
        <Toaster richColors position="top-center" />
        <Link to={listPath} className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to enquiries
        </Link>
        <Card className="p-6 text-sm text-destructive">{String(error || "Not found")}</Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-enquiry-detail-page">
      <Toaster richColors position="top-center" />
      <div className="flex items-center justify-between">
        <Link
          to={listPath}
          className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          data-testid="admin-enquiry-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to enquiries
        </Link>
        <Badge className={`border ${STATUS_TONE[doc.status] || STATUS_TONE.new}`} data-testid="admin-enquiry-status-badge">
          {STATUS_LABEL[doc.status] || doc.status}
        </Badge>
      </div>

      <Card className="p-5 md:p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Tag className="h-3.5 w-3.5" />
              <span data-testid="admin-enquiry-category">{doc.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-bold text-lg" data-testid="admin-enquiry-name">{doc.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              <a
                href={`mailto:${doc.email}`}
                className="underline text-primary"
                data-testid="admin-enquiry-email"
              >
                {doc.email}
              </a>
              <button
                onClick={copyEmail}
                className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                data-testid="admin-enquiry-copy-email"
              >
                {copied ? <><CheckCircle2 className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Submitted {formatDate(doc.submitted_at)}
              {doc.ip_address ? <span className="ml-2">· IP {doc.ip_address}</span> : null}
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Message</p>
          <div
            className="whitespace-pre-wrap rounded-lg border bg-muted/40 p-4 text-sm leading-relaxed"
            data-testid="admin-enquiry-message"
          >
            {doc.message}
          </div>
        </div>
      </Card>

      <Card className="p-5 md:p-6 space-y-4" data-testid="admin-enquiry-manage-card">
        <h2 className="text-lg font-bold">Manage this enquiry</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Status
            </label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="admin-enquiry-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground md:text-right md:pt-6">
            {doc.resolved_at ? (
              <span data-testid="admin-enquiry-resolved-meta">
                Resolved {formatDate(doc.resolved_at)}
                {doc.resolved_by ? <> · by <code className="text-[11px]">{doc.resolved_by.slice(0, 8)}</code></> : null}
              </span>
            ) : null}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Admin notes
          </label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes — not visible to the submitter."
            rows={5}
            className="mt-1"
            data-testid="admin-enquiry-notes"
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(listPath)}
            data-testid="admin-enquiry-cancel"
          >
            Back
          </Button>
          <Button
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid="admin-enquiry-save"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
            ) : (
              <><Save className="h-4 w-4 mr-1.5" /> Save changes</>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}
